/**
 * All Third-Parties Tab Component
 *
 * Displays comprehensive vendor list with server-side:
 * - Pagination (50 items per page)
 * - Search (debounced 500ms)
 * - Filtering (risk, status)
 * - Sorting (vendor name, risk, last assessed, total issues)
 *
 * Features:
 * - Vendor logos with fallback rendering
 * - Created By column with user avatars
 * - Issues by severity badges (clickable)
 * - Export functionality (stubbed)
 */

import { useGetTeamsListQuery } from '@/modules/auth/requests';
import { LoadingSpinner } from '@/shared/components/custom/spin';
import { ZTableDeprecrated } from '@/shared/components/table-deprecrated/TableDeprecrated';
import { ZTablePagination } from '@/shared/components/TablePagination';
import { SearchField } from '@/shared/components/TableSearch';
import { Select, SelectItem, cn, type SortDescriptor } from '@nextui-org/react';
import { useEffect, useMemo } from 'react';
import { getTPRAVendorsListColumns } from '../constants/columns';
import { useServerSideState } from '../hooks/useServerSideState';
import { useGetTPRAVendors } from '../requests/getTPRAVendors';
import type { TPRAVendorDetails } from '../types/vendors';

interface AllThirdPartiesTabProps {
  onVendorCountChange?: (count: number, isLoading: boolean) => void;
  onSeverityClick?: (vendorName: string, severity: string) => void;
}

export function AllThirdPartiesTab({
  onVendorCountChange,
  onSeverityClick,
}: AllThirdPartiesTabProps) {
  // Server-side state management
  const { queryParams, pagination, search, sort, filters } = useServerSideState({
    page: 1,
    page_size: 25,
  });

  // Fetch team data for member name mapping
  const { data: teamsData } = useGetTeamsListQuery({ isEnabled: true });
  const memberIdNameMapping = useMemo(
    () => teamsData?.memberIdNameMapping,
    [teamsData?.memberIdNameMapping]
  );

  // Fetch vendors from API with member mapping
  const { data, isLoading, isFetching, isError, error } =
    useGetTPRAVendors(queryParams, memberIdNameMapping);

  // Extract data with defaults
  const vendors = data?.vendors ?? [];
  const totalVendors = data?.total_vendors ?? 0;
  const totalPages = data?.total_pages ?? 0;
  const currentPage = data?.page ?? 1;

  // Memoize columns with severity click callback
  const columns = useMemo(
    () => getTPRAVendorsListColumns(onSeverityClick),
    [onSeverityClick]
  );

  // Notify parent component of vendor count changes
  useEffect(() => {
    if (onVendorCountChange) {
      onVendorCountChange(totalVendors, isFetching);
    }
  }, [totalVendors, isFetching, onVendorCountChange]);

  // Handle sort changes from table
  const handleSortModelChange = (sortDescriptor: SortDescriptor) => {
    const column = String(sortDescriptor.column);
    
    if (!column) {
      sort.resetSort();
      return;
    }

    // Map column names to API sort fields
    const columnToFieldMap: Record<string, string> = {
      vendor_product_name: 'vendor_name',
      risk_rating: 'risk_rating',
      last_assessed_at: 'last_assessed_at',
      total_issues: 'total_issues',
    };

    const sortBy = columnToFieldMap[column] as
      | 'vendor_name'
      | 'risk_rating'
      | 'last_assessed_at'
      | 'total_issues'
      | undefined;

    if (sortBy) {
      sort.setSort(sortBy);
    }
  };

  return (
    <div className="h-full w-full flex flex-col gap-4 overflow-hidden mb-4">
      {/* Header: Title + Count | Status Filter + Search */}
      <div className="flex flex-row justify-between items-center gap-2 flex-shrink-0">
        {/* Left: Title + Count */}
        <div className="flex flex-row items-center gap-2 flex-grow">
          <h2 className="font-['Inter'] font-medium text-[14px] leading-[20px] text-[#11181C]">
            All Third-Parties
          </h2>
          {!isLoading && !isFetching && (
            <div className="flex flex-row justify-center items-center py-[1px] px-2 gap-1 bg-[#F4F4F5] rounded-[45px]">
              <span className="font-['Inter'] font-medium text-[10px] leading-[16px] text-[#52525B]">
                {totalVendors}
              </span>
            </div>
          )}
        </div>

        {/* Right: Status Filter + Search */}
        <div className="flex flex-row items-center gap-2">
          <Select
            placeholder="All Statuses"
            selectedKeys={[filters.status ?? 'all']}
            onChange={(e) => {
              const value = e.target.value;
              filters.setStatus(
                value === 'all'
                  ? undefined
                  : (value as
                      | 'intake_form'
                      | 'due_diligence'
                      | 'review'
                      | 'reassessment'
                      | 'complete')
              );
            }}
            className="w-[160px]"
            classNames={{
              trigger: "h-[36px] bg-white border-1 border-[#E4E4E7] rounded-[6px]",
              value: "font-['Inter'] font-normal text-[14px] leading-[20px] text-[#3F3F46]",
            }}
          >
            <SelectItem key="all" value="all">
              All Statuses
            </SelectItem>
            <SelectItem key="intake_form" value="intake_form">
              New Assessment
            </SelectItem>
            <SelectItem key="due_diligence" value="due_diligence">
              Due Diligence
            </SelectItem>
            <SelectItem key="review" value="review">
              In Review
            </SelectItem>
            <SelectItem key="reassessment" value="reassessment">
              Reassessment
            </SelectItem>
            <SelectItem key="complete" value="complete">
              Reviewed
            </SelectItem>
          </Select>

          <SearchField
            value={search.searchTerm}
            onChange={search.setSearchTerm}
            placeholder="Search by name"
            className="w-[420px]"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="relative flex-1 min-h-0">
          {/* Loading overlay - only during refetch, not initial load */}
          {!isLoading && isFetching && (
            <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg">
              <LoadingSpinner />
            </div>
          )}
          <ZTableDeprecrated<TPRAVendorDetails>
          className={cn(
            'border-1 border-[#E4E4E7] rounded-lg overflow-auto scroll-auto flex-1'
          )}
          layout="fixed"
          loading={isLoading || isFetching}
          rows={vendors}
          columns={columns}
          isHeaderSticky
          isCompact
          removeWrapper
          getRowId={(row) => row.uid ?? row.id}
          tableHeaderProps={{ className: 'shadow-none rounded-none' }}
          tableRowProps={(row, rowIndex) => ({
            className: cn(rowIndex === 0 ? 'border-none' : 'border-y', 'group'),
          })}
          classNames={{
            wrapper: '!shadow-none !rounded-none',
          }}
          aria-label="TPRA Vendors List"
          onSortModelChange={handleSortModelChange}
          tableBodyProps={{
            emptyContent: isLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner
                  containerClassName="bg-transparent relative"
                  className="mx-auto"
                />
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-red-500 text-sm font-medium">
                  Failed to load vendors
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  {error?.message ?? 'An unexpected error occurred'}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-gray-500 text-sm">No vendors found</p>
              </div>
            ),
          }}
          />
        </div>
        <ZTablePagination
          totalPages={totalPages}
          paginationState={{
            currentPage: currentPage,
            rowsPerPage: pagination.pageSize,
          }}
          setPagination={{
            handlePageChange: (page) => pagination.setPage(page),
            handleRowsPerPageChange: (rows) => pagination.setPageSize(rows),
          }}
        />
      </div>
    </div>
  );
}
